import { useFormTemplateKeys } from '@commenda/api';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  Input,
  Label,
  SingleCombobox,
  useToggle,
} from '@commenda/kakashi';
import { THumanFormStepDefinition, TMapping } from '@commenda/types';
import { useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useFormBuilderLinks } from '../../context/FormBuilderLinksContext';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';
import { HumanFormBuilderLinks } from './HumanFormBuilderLinks';
import { HumanFormStepHelper } from './human-form-step-helper';
import { useHumanFormVersionState } from './useHumanFormVersionState';

export const HumanFormNodePropertyPanel = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();
  const addEditContextProjectionModalVisibility = useToggle(false);
  const [mappingToEdit, setMappingToEdit] = useState<TMapping | null>(null);
  const [projectionFormField, setProjectionFormField] = useState('');
  const [projectionContextKey, setProjectionContextKey] = useState('');

  const { keys, isLoading } = useFormTemplateKeys({
    hasLiveForm: true,
    limit: 1000,
  });

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  ) as THumanFormStepDefinition;

  // Both hosts' concerns arrive as data: the builder links only where a host provides them, and
  // the superseded-version check only when the step's form is not among the live ids.
  const formBuilderLinks = useFormBuilderLinks();
  const {
    supersededTemplate,
    liveFormId,
    error: versionError,
  } = useHumanFormVersionState({
    formId: selectedStep?.formId,
    keys,
  });

  if (!workflow) return <div>Workflow not found</div>;
  if (!selectedStep) return <div>Step not found</div>;

  const formTemplateKeyOptions = HumanFormStepHelper.buildFormTemplateOptions(
    keys,
    supersededTemplate
  );

  const updateHumanFormStep = (updates: Partial<THumanFormStepDefinition>) => {
    setWorkflow({
      ...workflow,
      steps: workflow?.steps.map((step) =>
        step.id === selectedEntity.id ? { ...step, ...updates } : step
      ),
    });
  };

  const onSaveContextProjection = () => {
    if (!projectionFormField.trim() || !projectionContextKey.trim()) return;

    // Create a simplified mapping that just stores the field path
    // The engine will read from outputData[formField] and write to context[contextKey]
    const mapping: TMapping = {
      id: mappingToEdit?.id || uuidV4(),
      targetPath: projectionContextKey.trim(),
      value: {
        type: 'field' as const,
        ref: {
          source: 'step' as any,
          stepId: selectedStep.id,
          path: projectionFormField.trim(),
        },
      },
    };

    const existingMapping = selectedStep.contextProjection?.find(
      (m) => m.id === mapping.id
    );
    if (existingMapping) {
      updateHumanFormStep({
        contextProjection: selectedStep.contextProjection?.map((m) =>
          m.id === mapping.id ? mapping : m
        ),
      });
    } else {
      updateHumanFormStep({
        contextProjection: [...(selectedStep.contextProjection || []), mapping],
      });
    }

    // Reset and close
    setProjectionFormField('');
    setProjectionContextKey('');
    setMappingToEdit(null);
    addEditContextProjectionModalVisibility.hide();
  };

  const openEditProjection = (mapping: TMapping) => {
    setMappingToEdit(mapping);
    setProjectionContextKey(mapping.targetPath || '');
    // Extract path from the mapping value
    const path =
      mapping.value?.type === 'field' && mapping.value?.ref?.path
        ? mapping.value.ref.path
        : mapping.targetPath || '';
    setProjectionFormField(path);
    addEditContextProjectionModalVisibility.show();
  };

  const openAddProjection = () => {
    setMappingToEdit(null);
    setProjectionFormField('');
    setProjectionContextKey('');
    addEditContextProjectionModalVisibility.show();
  };

  return (
    <div className="flex flex-col gap-4">
      <FieldWrapper>
        <Label>Form Template</Label>
        <SingleCombobox
          options={formTemplateKeyOptions}
          value={selectedStep.formId || ''}
          setValue={(value) => {
            updateHumanFormStep({ formId: value });
          }}
        />
        {liveFormId !== null && (
          <Alert
            variant="info"
            title="The live version of this form differs from the one selected"
            description="Runs of this workflow still open the version selected above."
            action={{
              label: 'Use live version',
              size: 'sm',
              onClick: () => updateHumanFormStep({ formId: liveFormId }),
            }}
          />
        )}
        {versionError !== undefined && (
          <p className="text-xs text-gray-500">
            The version of the selected form could not be checked.
          </p>
        )}
        {formBuilderLinks !== undefined && (
          <HumanFormBuilderLinks
            selectedFormId={selectedStep.formId || undefined}
            stepName={selectedStep.name}
            links={formBuilderLinks}
          />
        )}
      </FieldWrapper>

      {/* Context Projection */}
      <FieldWrapper>
        <Label className="flex justify-between items-center">
          <span className="grow flex items-center gap-2">
            Context Projection
            <Badge variant="blue" size="sm">
              New
            </Badge>
          </span>
          <Button size="sm" onClick={openAddProjection}>
            <Icons.Plus className="size-4" />
          </Button>
        </Label>
        <p className="text-xs text-gray-500 mb-2">
          Copy form field values to workflow context for use in subsequent
          steps.
        </p>
        <div className="flex flex-col gap-2">
          {selectedStep.contextProjection?.map((mapping) => {
            const sourcePath =
              mapping.value?.type === 'field' && mapping.value?.ref?.path
                ? mapping.value.ref.path
                : mapping.targetPath;
            return (
              <div
                key={mapping.id}
                className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded"
              >
                <div className="flex flex-col text-sm">
                  <span className="text-gray-500 text-xs">
                    {sourcePath} → {mapping.targetPath}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Icons.Edit01
                    className="size-4 cursor-pointer text-gray-500 hover:text-gray-700"
                    onClick={() => openEditProjection(mapping)}
                  />
                  <Icons.Trash02
                    className="size-4 cursor-pointer text-gray-500 hover:text-red-500"
                    onClick={() => {
                      updateHumanFormStep({
                        contextProjection:
                          selectedStep.contextProjection?.filter(
                            (m) => m.id !== mapping.id
                          ),
                      });
                    }}
                  />
                </div>
              </div>
            );
          })}
          {(!selectedStep.contextProjection ||
            selectedStep.contextProjection.length === 0) && (
            <div className="text-sm text-gray-500 italic">
              No context projection configured
            </div>
          )}
        </div>
      </FieldWrapper>

      {/* Simplified Context Projection Modal */}
      <Dialog
        open={addEditContextProjectionModalVisibility.visible}
        onOpenChange={(open) => {
          if (!open) addEditContextProjectionModalVisibility.hide();
        }}
      >
        <DialogContent className="flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {mappingToEdit ? 'Edit' : 'Add'} Context Projection
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4 px-6">
            <FieldWrapper>
              <Label>Form Field (Field ID)</Label>
              <Input
                value={projectionFormField}
                onChange={(e) => setProjectionFormField(e.target.value)}
                placeholder="e.g., registrationCountry or credentials.*"
              />
              <p className="text-xs text-gray-500 mt-1">
                The Field ID from Form Builder. Use{' '}
                <code className="bg-gray-100 px-1 rounded">prefix.*</code> to
                group all fields with that prefix (e.g.,{' '}
                <code className="bg-gray-100 px-1 rounded">credentials.*</code>)
              </p>
            </FieldWrapper>
            <FieldWrapper>
              <Label>Context Key</Label>
              <Input
                value={projectionContextKey}
                onChange={(e) => setProjectionContextKey(e.target.value)}
                placeholder="e.g., registrationCountry"
              />
              <p className="text-xs text-gray-500 mt-1">
                The key in workflow context where this value will be stored
              </p>
            </FieldWrapper>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={addEditContextProjectionModalVisibility.hide}
            >
              Cancel
            </Button>
            <Button
              onClick={onSaveContextProjection}
              disabled={
                !projectionFormField.trim() || !projectionContextKey.trim()
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
